import React, { useState, useEffect } from 'react';
import { verificationService } from '../../services/verification.service';
import { useAuth } from '../../contexts/AuthContext';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkIcon from '@mui/icons-material/Work';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ErrorIcon from '@mui/icons-material/Error';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CelebrationIcon from '@mui/icons-material/Celebration';
import './VerificationModal.css';

interface CompanyVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type VerificationStep = 'email-check' | 'benefits' | 'email' | 'code' | 'success' | 'not-found';

const CompanyVerificationModal: React.FC<CompanyVerificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState<VerificationStep>('email-check');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleEmailCheck = async () => {
    if (!email) {
      setError('Please enter your corporate email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verificationService.searchCompanyByEmail(email);
      
      if (response.found && response.company) {
        setCompanyInfo(response.company);
        if (response.can_verify) {
          setStep('benefits');
        } else {
          setError('Verification is not enabled for this company');
        }
      } else {
        setStep('not-found');
      }
    } catch (error: any) {
      setError('Failed to check email domain');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await verificationService.initiateVerification({
        corporate_email: email,
        company_id: companyInfo.id
      });
      
      if (response.success && response.verification_id) {
        setVerificationId(response.verification_id);
        setStep('code');
        setResendCooldown(60);
      } else {
        setError(response.message || 'Failed to send verification email');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to send verification email';
      setError(errorMessage);
      
      // Check if it's a rate limit error
      if (errorMessage.includes('Too many')) {
        setResendCooldown(300); // 5 minute cooldown for rate limiting
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verificationService.verifyCode({
        verification_id: verificationId,
        code: code
      });

      if (response.success) {
        // Complete the verification
        await verificationService.completeVerification(
          companyInfo.name,
          email
        );
        
        setStep('success');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 3000);
      } else {
        setError(response.message || 'Invalid code');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify code';
      setError(errorMessage);
      
      // Check if code expired or too many attempts
      if (errorMessage.includes('expired')) {
        setStep('email');
        setVerificationId('');
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      const response = await verificationService.initiateVerification({
        corporate_email: email,
        company_id: companyInfo.id
      });
      
      if (response.success && response.verification_id) {
        setVerificationId(response.verification_id);
        setResendCooldown(60);
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.className = 'resend-success';
        successMsg.textContent = 'New code sent!';
        document.querySelector('.verification-modal-content')?.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 3000);
      } else {
        setError(response.message || 'Failed to resend code');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const renderEmailCheck = () => (
    <div className="verification-step">
      <h2><LockIcon className="inline-icon" /> Employee Verification</h2>
      <p>Verify your employment to unlock the referral marketplace and earn rewards!</p>
      
      <form onSubmit={(e) => { e.preventDefault(); handleEmailCheck(); }}>
        <div className="form-group">
          <label htmlFor="corporate-email">Enter Your Corporate Email</label>
          <input
            id="corporate-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.name@company.com"
            required
            disabled={loading}
            autoFocus
          />
          <small>We'll automatically detect your company from your email domain</small>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="verification-actions">
          <button 
            type="submit"
            className="btn btn-primary"
            disabled={loading || !email}
          >
            {loading ? 'Checking...' : 'Continue'}
          </button>
          <button 
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  const renderBenefits = () => (
    <div className="verification-step">
      <div className="company-detected">
        <h3>Company Detected: {companyInfo?.name}</h3>
        <p className="email-display">{email}</p>
      </div>

      <h2>What you'll unlock:</h2>
      <div className="benefits-container">
        <ul className="benefits-list">
          <li>
            <CheckCircleIcon className="benefit-icon" />
            Verified {companyInfo?.name} employee badge
          </li>
          <li>
            <WorkIcon className="benefit-icon" />
            Access to the referral marketplace
          </li>
          <li>
            <MonetizationOnIcon className="benefit-icon" />
            Earn referral bonuses for successful hires
          </li>
          <li>
            <HandshakeIcon className="benefit-icon" />
            Connect with other verified professionals
          </li>
          <li>
            <EmojiEventsIcon className="benefit-icon" />
            Build your professional reputation
          </li>
        </ul>
      </div>

      <div className="verification-actions">
        <button 
          className="btn btn-primary"
          onClick={handleSendCode}
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send Verification Code'}
        </button>
        <button 
          className="btn btn-secondary"
          onClick={() => { setStep('email-check'); setEmail(''); setCompanyInfo(null); }}
          disabled={loading}
        >
          Change Email
        </button>
      </div>
    </div>
  );

  const renderCodeStep = () => (
    <div className="verification-step">
      <div className="company-header">
        <h3>{companyInfo?.name}</h3>
      </div>

      <h2>Enter Verification Code</h2>
      <p>We've sent a 6-digit code to <strong>{email}</strong></p>
      <p className="code-hint">Check your inbox (and spam folder)</p>

      <form onSubmit={(e) => { e.preventDefault(); handleCodeSubmit(); }}>
        <div className="form-group">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            required
            disabled={loading}
            className="code-input"
            autoFocus
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="verification-actions">
          <button 
            type="submit"
            className="btn btn-primary"
            disabled={loading || code.length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>
          <button 
            type="button"
            className="btn btn-secondary"
            onClick={() => { setStep('email-check'); setCode(''); setError(''); setEmail(''); }}
            disabled={loading}
          >
            Start Over
          </button>
        </div>

        <div className="resend-container">
          <button
            type="button"
            className="resend-link"
            onClick={handleResendCode}
            disabled={loading || resendCooldown > 0}
          >
            {resendCooldown > 0 
              ? `Resend code in ${resendCooldown}s` 
              : "Didn't receive the code? Resend"
            }
          </button>
        </div>
      </form>
    </div>
  );

  const renderNotFound = () => (
    <div className="verification-step">
      <ErrorIcon className="not-found-icon" />
      <h2>Company Not Recognized</h2>
      <p>We couldn't recognize the company domain for <strong>{email}</strong></p>
      
      <div className="info-box">
        <p>This could mean:</p>
        <ul>
          <li>Your company hasn't been added to our system yet</li>
          <li>You're using a personal email instead of corporate email</li>
          <li>The email domain is not registered with your company</li>
        </ul>
      </div>

      <div className="help-text">
        <p><LightbulbIcon className="inline-icon small" /> Try using your official company email address</p>
        <p>Common formats: @company.com, @company.io, etc.</p>
      </div>

      <div className="verification-actions">
        <button 
          className="btn btn-primary"
          onClick={() => { setStep('email-check'); setEmail(''); }}
        >
          Try Another Email
        </button>
        <button 
          className="btn btn-secondary"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="verification-step success-step">
      <CheckCircleIcon className="success-icon" />
      <h2>Verification Complete!</h2>
      <p>You're now a verified employee of <strong>{companyInfo?.name}</strong></p>
      <p className="success-message">Welcome to the referral marketplace!</p>
      <div className="loading-spinner">Redirecting...</div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="verification-modal-overlay" onClick={onClose}>
      <div className="verification-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        {step === 'email-check' && renderEmailCheck()}
        {step === 'benefits' && renderBenefits()}
        {step === 'code' && renderCodeStep()}
        {step === 'not-found' && renderNotFound()}
        {step === 'success' && renderSuccess()}
      </div>
    </div>
  );
};

export default CompanyVerificationModal;